import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { getBeeWiseAssistantResponse, getQuickResponse } from '../../services/openaiService';

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Hi! I\'m Bee 🐝, your AI assistant at BeeWise. How can I help you today?',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const quickActions = [
    { label: 'What\'s next?', icon: 'ArrowRight', type: 'next_steps' },
    { label: 'Help with tasks', icon: 'HelpCircle', type: 'help' },
    { label: 'Company info', icon: 'Info', type: 'company_info' },
    { label: 'Contact HR', icon: 'Mail', type: 'hr' }
  ];

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSendMessage = async () => {
    if (inputValue?.trim() === '') return;

    const newMessage = {
      id: messages?.length + 1,
      type: 'user',
      text: inputValue,
      timestamp: new Date()
    };

    setMessages([...messages, newMessage]);
    setInputValue('');
    setIsTyping(true);
    setError(null);

    // Add to conversation history
    const updatedHistory = [
      ...conversationHistory,
      { role: 'user', content: inputValue }
    ];

    try {
      let botMessageText = '';
      const botMessageId = messages?.length + 2;

      // Stream response
      await getBeeWiseAssistantResponse(
        inputValue,
        conversationHistory,
        (chunk) => {
          botMessageText += chunk;
          setMessages((prev) => {
            const existingMessage = prev?.find(m => m?.id === botMessageId);
            if (existingMessage) {
              return prev?.map(m => 
                m?.id === botMessageId 
                  ? { ...m, text: botMessageText }
                  : m
              );
            } else {
              return [...prev, {
                id: botMessageId,
                type: 'bot',
                text: botMessageText,
                timestamp: new Date()
              }];
            }
          });
        },
        (fullResponse) => {
          setIsTyping(false);
          // Update conversation history
          setConversationHistory([
            ...updatedHistory,
            { role: 'assistant', content: fullResponse }
          ]);
        }
      );
    } catch (err) {
      console.error('Error sending message:', err);
      setIsTyping(false);
      
      // Enhanced error handling with specific error types
      let errorDisplayMessage = '❌ An error occurred. Please try again.';
      let errorType = 'general';
      
      if (err?.type === 'QUOTA_EXCEEDED') {
        errorDisplayMessage = err?.userMessage || '⚠️ OpenAI API quota exceeded. Please check your billing details at platform.openai.com/account/billing';
        errorType = 'quota';
        setError('API Quota Exceeded - Check your OpenAI billing settings');
      } else if (err?.type === 'AUTHENTICATION_ERROR') {
        errorDisplayMessage = err?.userMessage || '🔐 API authentication failed. Please verify your OpenAI API key in the .env file.';
        errorType = 'auth';
        setError('Authentication Error - Invalid API Key');
      } else if (err?.type === 'SERVICE_UNAVAILABLE') {
        errorDisplayMessage = err?.userMessage || '⏳ OpenAI service is temporarily unavailable. Please try again shortly.';
        errorType = 'service';
        setError('Service Temporarily Unavailable');
      } else {
        errorDisplayMessage = err?.userMessage || '❌ Unexpected error. Please try again or contact support.';
        setError('Connection Error - Please Try Again');
      }
      
      // Add detailed error message
      const errorMessage = {
        id: messages?.length + 2,
        type: 'bot',
        text: errorDisplayMessage,
        timestamp: new Date(),
        isError: true,
        errorType: errorType
      };
      setMessages((prev) => [...prev, errorMessage]);
      
      // Add helpful guidance for quota errors
      if (errorType === 'quota') {
        const guidanceMessage = {
          id: messages?.length + 3,
          type: 'bot',
          text: '💡 To fix this:\n1. Visit platform.openai.com/account/billing\n2. Add a payment method if not already added\n3. Check your usage limits and upgrade plan if needed\n4. Verify your API key is associated with an active billing account',
          timestamp: new Date(),
          isError: false
        };
        setMessages((prev) => [...prev, guidanceMessage]);
      } else if (errorType === 'auth') {
        const guidanceMessage = {
          id: messages?.length + 3,
          type: 'bot',
          text: '💡 To fix this:\n1. Check your .env file has VITE_OPENAI_API_KEY set\n2. Verify the API key is valid at platform.openai.com/api-keys\n3. Generate a new API key if needed\n4. Restart your development server after updating .env',
          timestamp: new Date(),
          isError: false
        };
        setMessages((prev) => [...prev, guidanceMessage]);
      }
    }
  };

  const handleQuickAction = async (action) => {
    if (!action?.type) {
      setInputValue(action?.label);
      if (inputRef?.current) {
        inputRef?.current?.focus();
      }
      return;
    }

    setIsTyping(true);
    setError(null);

    const userMessage = {
      id: messages?.length + 1,
      type: 'user',
      text: action?.label,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMessage]);

    try {
      const response = await getQuickResponse(action?.type);
      
      const botMessage = {
        id: messages?.length + 2,
        type: 'bot',
        text: response,
        timestamp: new Date()
      };

      setMessages((prev) => [...prev, botMessage]);
      setIsTyping(false);

      // Update conversation history
      setConversationHistory([
        ...conversationHistory,
        { role: 'user', content: action?.label },
        { role: 'assistant', content: response }
      ]);
    } catch (err) {
      console.error('Error with quick action:', err);
      setIsTyping(false);
      
      // Enhanced error handling for quick actions
      let errorDisplayMessage = '❌ Unable to process quick action.';
      
      if (err?.type === 'QUOTA_EXCEEDED') {
        errorDisplayMessage = err?.userMessage || '⚠️ API quota exceeded. Please check your OpenAI billing.';
        setError('API Quota Exceeded');
      } else if (err?.type === 'AUTHENTICATION_ERROR') {
        errorDisplayMessage = err?.userMessage || '🔐 API authentication failed.';
        setError('Authentication Error');
      } else {
        errorDisplayMessage = err?.userMessage || '❌ Quick action failed. Try typing your question instead.';
        setError('Quick Action Failed');
      }
      
      const errorMessage = {
        id: messages?.length + 2,
        type: 'bot',
        text: errorDisplayMessage,
        timestamp: new Date(),
        isError: true
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <button
        onClick={handleToggle}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary rounded-full shadow-elevation-3 hover:shadow-glow hover:scale-110 transition-all duration-200 flex items-center justify-center group"
        aria-label="Toggle AI assistant"
      >
        <Icon 
          name={isOpen ? 'X' : 'MessageCircle'} 
          size={24} 
          color="var(--color-primary-foreground)" 
          strokeWidth={2.5}
        />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-background animate-pulse" />
      </button>
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] h-[600px] max-h-[calc(100vh-8rem)] bg-card rounded-lg shadow-elevation-3 border border-border flex flex-col animate-scale-in">
          <div className="flex items-center justify-between p-4 border-b border-border bg-secondary/20 rounded-t-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="Bot" size={20} color="var(--color-primary)" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Bee AI Assistant 🐝</h3>
                <span className="text-xs text-success flex items-center gap-1">
                  <span className="w-2 h-2 bg-success rounded-full" />
                  {error ? 'Connection Error' : 'Online'}
                </span>
              </div>
            </div>
            <button
              onClick={handleToggle}
              className="p-2 hover:bg-muted/50 rounded-lg transition-colors duration-150"
              aria-label="Close chat"
            >
              <Icon name="Minimize2" size={18} color="var(--color-muted-foreground)" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                <div className="flex items-center gap-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
                  <span>{error}</span>
                </div>
              </div>
            )}
            {messages?.map((message) => (
              <div
                key={message?.id}
                className={`flex ${message?.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message?.type === 'user' ?'bg-primary text-primary-foreground' 
                      : message?.isError 
                        ? 'bg-destructive/10 text-destructive border border-destructive/20' :'bg-muted text-foreground'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message?.text}</p>
                  <span className="text-xs opacity-70 mt-1 block">
                    {message?.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg p-3 flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 border-t border-border space-y-3">
            <div className="flex flex-wrap gap-2">
              {quickActions?.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickAction(action)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-muted/50 hover:bg-muted rounded-lg text-xs text-muted-foreground hover:text-foreground transition-colors duration-150"
                >
                  <Icon name={action?.icon} size={14} color="currentColor" />
                  {action?.label}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150"
              />
              <Button
                onClick={handleSendMessage}
                variant="default"
                size="icon"
                disabled={inputValue?.trim() === '' || isTyping}
                className="shrink-0"
              >
                <Icon name="Send" size={18} color="var(--color-primary-foreground)" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;